import { CommonModule, NgIf } from '@angular/common'
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core'
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  startWith,
  Subscription,
  take,
  tap
} from 'rxjs'
import {
  selectCtc,
  selectCtcValue,
  selectRevisedCtc
} from '../../../../store/employee-salary/employee-salary.selectors'
import { salaryRevisionActions } from '../../../../store/employee-salary/employee-salary.actions'
import { CTC, SalaryMode } from '../../../../store/employee-salary/employee-salary.state'
import { IndianNumberInputFormatDirective } from '../../../../directives/indian-number-input-format.directive'
import dayjs from 'dayjs'
import { ActivatedRoute } from '@angular/router'
import { selectLatestUserPayrollDate } from '../../../../store/payroll/payroll.reducers'
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'

@Component({
  selector: 'app-ctc-revision',
  imports: [
    NgIf,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    IndianNumberInputFormatDirective
  ],
  templateUrl: './ctc-revision.component.html',
  styleUrl: './ctc-revision.component.scss'
})
export class CtcRevisionComponent implements OnInit, OnDestroy {
  private store = inject(Store)
  private route = inject(ActivatedRoute)
  private fb = inject(FormBuilder)
  form!: FormGroup
  currentMode!: SalaryMode
  salaryMode = SalaryMode
  minPayOutFromMonth$!: Observable<string>
  minEffectiveFromDate!: string
  latestPayrollDate$ = this.store.select(selectLatestUserPayrollDate)
  //Following "currentCtcValue" is the value of user's current CTC
  //and will be fetched from the store
  //CTC value inserted in this component's template is the value of user's revised CTC
  currentCtcValue!: number
  @Output() formValuesChanged = new EventEmitter<CTC>()
  private subscription!: Subscription
  private latestPayrollDateValue = ''
  private previousPayoutFrom = ''
  ngOnInit(): void {
    this.currentMode = this.route.snapshot.data['mode'] as SalaryMode
    this.initializeForm()
    this.setupMinimumPayoutFromMonth()

    // Emit live ctc value for Parent Component
    this.subscription =
      this.form.valueChanges.subscribe(value => {
        this.formValuesChanged.emit(value)
      }) ?? new Subscription()

    this.store
      .select(selectCtcValue)
      .pipe(take(1))
      .subscribe(ctcValue => {
        this.currentCtcValue = ctcValue
      })

    this.store
      .select(selectCtc)
      .pipe(take(1))
      .subscribe(ctc => {
        if (ctc.effectiveFrom) {
          // //if (this.currentMode === SalaryMode.REVISE) {
          // this.minEffectiveFromDate = dayjs(ctc.effectiveFrom)
          //   .add(1, 'month')
          //   .startOf('month')
          //   .format('YYYY-MM-DD')
          // //this.form.patchValue({ effectiveFrom: this.minEffectiveFromDate }, { emitEvent: true })
          // //} else this.minEffectiveFromDate = ctc.effectiveFrom

          if (this.currentMode === SalaryMode.REVISE) {
            this.minEffectiveFromDate = dayjs().startOf('month').format('YYYY-MM-DD')
          } else {
            // this.minEffectiveFromDate = ctc.effectiveFrom
            this.minEffectiveFromDate = dayjs(ctc.effectiveFrom)
              .add(1, 'month')
              .startOf('month')
              .format('YYYY-MM-DD')
          }
          this.form.get('effectiveFrom')?.updateValueAndValidity({
            emitEvent: false
          })
          this.validateEffectiveFromDate()
        }
      })

    localStorage.setItem('currentCtc', this.currentCtcValue.toString())

    this.store
      .select(selectRevisedCtc)
      .pipe(take(1))
      .subscribe(ctc => {
        if (ctc) {
          if (ctc.effectiveFrom && this.currentMode === SalaryMode.REVISE) {
            if (+this.currentCtcValue !== ctc.ctc) {
              this.form.patchValue(
                { ctc: ctc.ctc, effectiveFrom: ctc.effectiveFrom, payoutFrom: ctc.payoutFrom },
                { emitEvent: true }
              )
            } else {
              this.form.patchValue(
                { ctc: ctc.ctc, effectiveFrom: '', payoutFrom: '' },
                { emitEvent: true }
              )
            }
          } else if (ctc.effectiveFrom && this.currentMode === SalaryMode.UPDATE) {
            this.form.patchValue(
              { ctc: ctc.ctc, effectiveFrom: ctc.effectiveFrom, payoutFrom: ctc.payoutFrom },
              { emitEvent: true }
            )
          } else this.form.patchValue(ctc, { emitEvent: true })
        }
      })

    // const payoutControl = this.form.get('payoutFrom')

    // this.previousPayoutFrom = payoutControl?.value ?? ''

    // payoutControl?.valueChanges.subscribe(value => {
    //   if (!value) return

    //   const minMonth = this.getMinPayoutMonth()

    //   if (minMonth && value < minMonth) {
    //     const previousValue = this.previousPayoutFrom

    //     if (previousValue && previousValue >= minMonth) {
    //       payoutControl.setValue(previousValue, { emitEvent: false })
    //     } else {
    //       payoutControl.setValue('', { emitEvent: false })
    //     }

    //     payoutControl.setErrors({
    //       ...(payoutControl.errors ?? {}),
    //       minMonth: true
    //     })

    //     return
    //   }

    //   this.previousPayoutFrom = value
    // })
  }

  ngOnDestroy() {
    this.store.dispatch(salaryRevisionActions.updateCTC({ ctc: this.form.value }))
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  initializeForm() {
    this.form = this.fb.group({
      basis: ['annual', Validators.required],
      currency: ['INR', Validators.required],
      ctc: [0, Validators.min(1)],
      effectiveFrom: ['', [Validators.required, this.minDateValidator()]],
      payoutFrom: ['', Validators.required]
    })
  }

  setupMinimumPayoutFromMonth() {
    this.minPayOutFromMonth$ = combineLatest([
      this.form.get('effectiveFrom')!.valueChanges.pipe(
        //startWith(this.form.get('effectiveFrom')?.value ?? this.minEffectiveFromDate), // Emit initial value
        map(effectiveDateValue => {
          const dateStringToUse = effectiveDateValue ?? this.minEffectiveFromDate
          if (dateStringToUse) {
            const dateObj = new Date(dateStringToUse)
            if (!isNaN(dateObj.getTime())) {
              return dateObj.toISOString().slice(0, 7)
            }
          }
          return new Date().toISOString().slice(0, 7)
        })
      ),
      this.latestPayrollDate$.pipe(startWith(null))
    ]).pipe(
      map(([effectiveFromMonth, latestPayrollMonth]) => {
        if (latestPayrollMonth) {
          return effectiveFromMonth > latestPayrollMonth ? effectiveFromMonth : latestPayrollMonth
        } else {
          return effectiveFromMonth
        }
      }),
      distinctUntilChanged(),
      tap(minMonth => {
        this.latestPayrollDateValue = minMonth
        const payoutFromControl = this.form.get('payoutFrom')
        const currentPayoutValue = payoutFromControl?.value

        if (!currentPayoutValue || currentPayoutValue < minMonth) {
          payoutFromControl?.patchValue(minMonth, { emitEvent: true })
        } else {
          payoutFromControl?.updateValueAndValidity({ emitEvent: false })
        }
      })
    )
  }

  getErrorMessage(controlName: string): string | null {
    const control = this.form.get(controlName)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
    }
    return null // No error
  }

  updateCtcAmount(event: Event) {
    const inputElement = event.target as HTMLInputElement
    const percentage = Number(inputElement.value)
    if (isNaN(percentage)) return

    const currentCtc = Number(this.currentCtcValue) || 0
    const increaseAmount = (currentCtc * percentage) / 100
    const revisedCtc = currentCtc + increaseAmount
    const finalCtc = Math.round(revisedCtc)

    this.form.patchValue({ ctc: finalCtc })
    // ✅ Save current CTC in localStorage
    // localStorage.setItem('currentCtc', currentCtc.toString())
  }

  updatePercentage(event: Event): void {
    const value = (event.target as HTMLInputElement).value

    const revisedCtc = Number(value.replace(/,/g, ''))

    if (!revisedCtc || !this.currentCtcValue) {
      return
    }

    const currentCtc = Number(this.currentCtcValue.toString().replace(/,/g, ''))

    const percentage = ((revisedCtc - currentCtc) / currentCtc) * 100

    const percentInput = document.getElementById('percentIncrease') as HTMLInputElement

    if (percentInput) {
      percentInput.value = percentage.toFixed(2)
    }
  }

  private minDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      const minDate = this.minEffectiveFromDate

      if (!value || !minDate) {
        return null
      }

      return value < minDate ? { minDate: { min: minDate, actual: value } } : null
    }
  }

  // validateEffectiveFromDate(): void {
  //   const control = this.form.get('effectiveFrom')
  //   control?.updateValueAndValidity()
  // }

  // validateEffectiveFromDate(): void {
  //   const control = this.form.get('effectiveFrom')
  //   if (!control) return

  //   control.updateValueAndValidity({ emitEvent: false })

  //   if (control.hasError('minDate')) {
  //     control.setValue('', { emitEvent: false })
  //     control.markAsTouched()
  //     control.updateValueAndValidity({ emitEvent: false })
  //   }
  // }

  validateEffectiveFromDate(): void {
    const effectiveFromControl = this.form.get('effectiveFrom')
    const payoutFromControl = this.form.get('payoutFrom')

    if (!effectiveFromControl || !payoutFromControl) return

    effectiveFromControl.updateValueAndValidity({ emitEvent: false })

    if (effectiveFromControl.hasError('minDate')) {
      // Dono fields reset karo
      effectiveFromControl.setValue('', { emitEvent: false })
      payoutFromControl.setValue('', { emitEvent: false })

      // Required error ke saath form invalid rahega
      effectiveFromControl.markAsTouched()
      payoutFromControl.markAsTouched()

      effectiveFromControl.updateValueAndValidity({ emitEvent: false })
      payoutFromControl.updateValueAndValidity({ emitEvent: false })

      // Parent form ki validity bhi recalculate karo
      this.form.updateValueAndValidity({ emitEvent: true })
    }
  }

  private getMinPayoutMonth(): string {
    const effectiveFrom = this.form.get('effectiveFrom')?.value

    if (!effectiveFrom) return ''

    const effectiveMonth = effectiveFrom.slice(0, 7)
    const latestPayrollMonth = this.latestPayrollDateValue ?? ''

    return latestPayrollMonth > effectiveMonth ? latestPayrollMonth : effectiveMonth
  }
}
